import { concat, forOwn, isNil, omitBy, toInteger } from 'lodash';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import isZocdocBrand from '../../utils/isZocdocBrand';
import { parseSearchGql } from './parseSearchGql';

export const SEARCH_QUERY = gql`
    query search(
        $includeCustomProviderProps: Boolean!
        $includeSpo: Boolean!
        $insuranceCarrierId: String!
        $insurancePlanId: String!
        $parameters: SearchParameters!
        $specialtyId: String!
    ) {
        insuranceCarrierImage(carrierId: $insuranceCarrierId)
        search(parameters: $parameters) {
            searchResponse {
                searchRequestId
                providerLocations {
                    ...providerLocation
                    provider {
                        properties @include(if: $includeCustomProviderProps) {
                            key
                            value
                        }
                    }
                }
                facets {
                    facetId
                    displayName
                    isMulti
                    selectionRequired
                    options {
                        optionId
                        displayName
                        count
                    }
                }
                governmentInsuranceData {
                    showGovernmentInsuranceWarning
                    header
                    message
                    links {
                        name
                        url
                    }
                }
                totalCount
                searchUrl
            }
            spo @include(if: $includeSpo) {
                spoAds {
                    spoAdDecisionId
                    adDecisionToken
                    adRank
                    providerLocation {
                        ...providerLocation
                    }
                }
                telehealthAds {
                    adDecisionId
                    decisionToken
                    provider {
                        allowedPatient
                        coupon {
                            couponCode
                            discountedCost
                        }
                        fullName
                        providerImage
                        readableProviderType
                        branchDeepLink
                        sourceId
                    }
                    searchResultId
                    spoServiceAttributionUrl
                    spoServiceImpressionUrl
                }
            }
            seo {
                description
                header {
                    type
                    content
                    label
                }
                related {
                    sections {
                        title
                        links {
                            url
                            text
                            tooltip
                        }
                    }
                }
                breadcrumbs {
                    text
                    url
                    image
                }
                title
                blurb {
                    header
                    description
                }
                abResponse {
                    assignments {
                        key
                        value
                    }
                }
                salpHeader {
                    title
                    caption
                }
                salpBlock {
                   section1
                   section2
                   section3
                }
            }
            searchOrigin {
                latitude
                longitude
            }
        }
    }

    fragment providerLocation on ProviderLocation {
        id
        provider {
            id
            monolithId
            approvedFullName
            averageRating
            canHaveAppointments
            circlePictureUrl
            defaultProcedureId
            firstName
            isPreview
            lastName
            nameInSentence
            onlySeesChildren
            postnominal
            prenominal
            profileUrl
            squarePictureUrl
            status
            relevantSpecialty(searchSpecialtyId: $specialtyId) {
                id
                name
            }
            procedures {
                id
            }
            reviewCount
            representativeReview {
                comment
            }
            specialties {
                id
                name
            }
        }
        location {
            id
            monolithId
            address1
            address2
            city
            latitude
            longitude
            name
            phone
            state
            zipCode
        }
        practice {
            id
            isFeePerBooking
            isUrgentCare
        }
        acceptsInsurance(carrierId: $insuranceCarrierId, planId: $insurancePlanId)
        distance(unit: "mi")
        searchResultId
    }
`;

export const gqlOptions = props => {
    const {
        useFacetedSearch = false,
        metadata,
        pageSize,
        parameters,
    } = props;

    const {
        address,
        carrierId: insuranceCarrierId = '-1',
        dayFilter,
        directoryId,
        filters,
        genderFilter,
        geoShape,
        ip,
        languageId: stringLanguageId,
        latitude,
        longitude,
        mockSearchGql: latencyTestDelayMs,
        normalizedUrl,
        pageOffset,
        placemarkId,
        planId: insurancePlanId = '-1',
        practiceId,
        procedureId,
        searchQuery,
        showProvidersWhoSeeChildren,
        showTimesAfter5,
        showTimesBefore10,
        sortType: rankBy,
        specialtyId,
        threatLevel,
        searchType,
        testSpoServiceDirect,
    } = parameters;


    const { sessionId, trackingId, searchQueryGuid } = metadata;

    const providerGender = parseGender(genderFilter);
    const timeFilter = parseTimeFilter({
        showTimesAfter5,
        showTimesBefore10,
    });
    const languageId = toInteger(stringLanguageId) > 0 ? toInteger(stringLanguageId) : null;

    const itemOffset = pageOffset * pageSize;
    const itemQuantity = pageSize;

    const coordinates = latitude && longitude
        ? {
            latitude,
            longitude,
        }
        : null;

    const searchLocation = {
        address,
        coordinates,
        geoShape,
        ip,
    };

    const searchParams = omitBy({
        dayFilter,
        directoryId,
        insuranceCarrierId,
        insurancePlanId,
        itemOffset,
        itemQuantity,
        languageId,
        latencyTestDelayMs,
        normalizedUrl,
        placemarkId,
        practiceId,
        procedureId,
        providerGender,
        rankBy,
        searchLocation,
        searchQuery,
        searchQueryGuid,
        sessionId,
        specialtyId,
        threatLevel,
        timeFilter,
        trackingId,
        searchType,
        testSpoServiceDirect,
    }, isNil);

    /**
    * We don't want to send this unless it's explicitly set as true, because
    * "false" has a different meaning than unset. Long-term, the filter UI for this
    * probably needs to be changed to a dropdown instead.
    *
    * https://jira.zocdoc.local/jira/browse/TRIAG-11919
    */
    if (showProvidersWhoSeeChildren) {
        searchParams.seesChildren = true;
    }

    searchParams.requestedFacets = ['procedures'];

    if (useFacetedSearch) {
        searchParams.requestedFacets = concat(searchParams.requestedFacets, ['sex', 'hospital_affiliations', 'languages', 'sees_children', 'day_availability', 'special_hours']);
        searchParams.filters = transformFilters(filters);

        if (procedureId !== '-1') {
            searchParams.filters.push({
                name: 'procedures',
                values: [procedureId],
            });
        }
    }

    return {
        errorPolicy: 'all',
        ssr: true,
        variables: {
            includeCustomProviderProps: !isZocdocBrand(directoryId),
            includeSpo: isZocdocBrand(directoryId),
            insuranceCarrierId,
            insurancePlanId,
            parameters: searchParams,
            specialtyId,
        },
    };
};

export const withSearchResults = graphql(SEARCH_QUERY, {
    notifyOnNetworkStatusChange: true,
    options: gqlOptions,
    props: parseSearchGql,
});

const parseGender = gender => {
    switch (gender) {
        case '1':
            return 'Male';

        case '2':
            return 'Female';

        default:
            return null;
    }
};

const parseTimeFilter = ({ showTimesAfter5, showTimesBefore10 }) => {
    if (showTimesAfter5 && showTimesBefore10) {
        return 'Before10AMOrAfter5PM';
    } else if (showTimesBefore10) {
        return 'Before10AM';
    } else if (showTimesAfter5) {
        return 'After5PM';
    } else {
        return 'AnyTime';
    }
};

/**
 * Transform filters into the correct format for directory-service.
 *
 * Example:
 * input - { facetId: [optionId] }
 * output - [{ name: 'facetId', values: [optionId] }]
 */
const transformFilters = filters => {
    const filtersTransformedIntoArray = [];

    forOwn(filters, (value, key) => {
        if (value.length > 0) {
            filtersTransformedIntoArray.push({
                name: key,
                values: value,
            });
        }
    });

    return filtersTransformedIntoArray;
};
